import {type PIXI} from '@colonist/pixi'
import {SpriteView} from '../../ui_canvas_engine/SpriteView.ts'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler.ts'
import {TextView} from '../../ui_canvas_engine/TextView.ts'
import {GameFont} from '../../GameFont.ts'
import {GameColor} from '../../GameColor.ts'
import {UIGameHoverResourceInfoView} from '../UIGameHoverResourceInfoView.ts'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView.ts'
import {SpriteButtonView} from '../../ui_canvas_engine/SpriteButtonView.ts'
import {type TranslationObject} from '@colonist/i18n'
import {UIGameShakeAnimation} from '../../ui_canvas_engine/animation/UIGameShakeAnimation.ts'
import {type UIGameAnimationBase} from '../../ui_canvas_engine/animation/UIGameAnimationBase.ts'
import {Point} from '@colonist/game-mechanic-validators'
import {type CardEnum, type PlayerColor} from '@colonist/game-states'
import {type UIGameMechanic} from '../UIGameEventController.ts'
import {GameStateEnums} from '../../../../../shared/GameEnums.ts'
import {type UIHoverActionController} from '../ui_game_hover_actions/UIHoverActionController.ts'
import {type UIGameManager} from '../UIGameManager.ts'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export class UIPlacementConfirmationButton extends SectionBgView implements UIGameMechanic {
    center: Point

    private readonly button: SpriteButtonView
    private buttonShakeAnimation: UIGameAnimationBase

    private resourceCardStates: UIGameHoverResourceInfoView | undefined
    private canTakeAction: boolean
    private readonly uiGameManager: UIGameManager
    private readonly hoverActionController: UIHoverActionController | undefined
    private readonly placementIndex: number

    constructor(x: number, y: number, w: number, h: number, container: PIXI.Container, uiGameManager: UIGameManager, action: () => void, texture: PIXI.Texture,
                canTakeAction: boolean, myColor: PlayerColor, bankActionAmount: number | undefined, hoverActionController: UIHoverActionController | undefined, placementIndex: number) {
        super(x, y, w, h, container, true)
        this.uiGameManager = uiGameManager
        this.canTakeAction = canTakeAction
        this.hoverActionController = hoverActionController
        this.placementIndex = placementIndex
        this.button = new SpriteButtonView(0, 0, w, h, this as any as PIXI.Container)
        this.button.updateAction(() => {
            if(this.canTakeAction) return action()

            this.shakeButton()
            this.resourceCardStates?.flickerMissingCards()
        })
        this.center = new Point(x + w / 2, y + h / 2)
        this.button.addCenteredIcon(texture)
        this.addBadge(myColor, canTakeAction, bankActionAmount)

        if(canTakeAction) return
        this.button.inactivateOpacity()

        this.uiGameManager.eventController.registerUIGameMechanic(this)
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.PlayerControllerStateUpdated: this.updateButtonState(); break
            default: break
        }
    }

    destroy() {
        this.uiGameManager.eventController.unregisterUIGameMechanic(this)
        super.destroy()
    }

    private shakeButton() {
        if(this.buttonShakeAnimation != undefined) this.buttonShakeAnimation.stop()
        this.buttonShakeAnimation = new UIGameShakeAnimation(this.button).start()
    }

    private addBadge(myColor: PlayerColor, canTakeAction: boolean, amount?: number) {
        if(amount === undefined) return

        const size = this.height / 3
        const buttonBorderX = this.width * 0.1
        const buttonBorderY = this.height * 0.04
        const badgeX = this.width - size
        const badgeTexture = UIGameSpriteHandler.getButtonBadgeForPlayer(myColor)
        const badgeSprite = new SpriteView(badgeX - buttonBorderX, buttonBorderY, size, size, this.button.viewContainer as any as PIXI.Container, badgeTexture)
        this.button.addAsset(badgeSprite)

        this.createCardCountText(amount)
    }

    private createCardCountText(count: number): TextView {
        const buttonBorderX = this.width * 0.1
        const buttonBorderY = this.height * 0.04
        const countText: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: count}}
        const countTextView = new TextView(countText, this.width * 0.8 - buttonBorderX, buttonBorderY, this.width, this.height,
            this.button.viewContainer as any as PIXI.Container, undefined, undefined, false)

        const fontSize = this.height / 5.5
        const maxNumberForOneDigitText = 9
        if(count > maxNumberForOneDigitText) countTextView.x -= this.width / 15

        const colorDarkGrey = GameColor.getColor(GameColor.Color.DarkGrey)
        const colorOffWhite = GameColor.getColor(GameColor.Color.OffWhite)

        countTextView.changeStyle({
            fontSize: fontSize + 'px',
            fill: colorOffWhite,
            fontWeight: GameFont.Weight.Bold,
            stroke: colorDarkGrey,
            strokeThickness: 2,
        })

        return countTextView
    }

    addResourceCardStates(cardEnums: CardEnum[], cardInventory: CardEnum[]) {
        this.resourceCardStates = new UIGameHoverResourceInfoView(cardEnums, this, this as any as PIXI.Container, cardInventory, false)
    }

    private updateButtonState() {
        if(!this.hoverActionController) return

        const updatedParameters = this.hoverActionController.getConfirmationParameters(this.placementIndex)
        const cardInventory = this.uiGameManager.cardController.getCardInventory()

        this.canTakeAction = updatedParameters.canTakeAction

        if(updatedParameters.canTakeAction) {
            this.button.activateOpacity()
        } else {
            this.button.inactivateOpacity()
        }

        this.resourceCardStates?.updateCardViews(cardInventory)
    }
}
